import { createMachine, MachineConfig } from 'xstate';
import creditCardStates from '../submitCase/creditCard';
import selectPaymentTypeStates, {
  PaymentType,
} from '../submitCase/selectPaymentType';
import checkoutStates from './checkout';
import * as guards from './guards';
import * as actions from './actions';
import { uploadSitesWithProgress } from './actions';
import { EditCaseEvent, EditCaseContext } from './types';
import * as RDBCases from '../../services/buildfire/rdb/cases';
import { getDatabase } from 'firebase/database';
import caseTypeStates from '../submitCase/caseType';
import { loadEditCaseDraft } from '../../services/sessionStorage/editCaseDraft';

async function loadCase(context: EditCaseContext) {
  const db = getDatabase(context.firebaseApp);
  const c = await RDBCases.getById(db, context.caseId);

  const invoice = await RDBCases.getInvoice(db, context.caseId);

  const draft = loadEditCaseDraft(context.caseId);

  return { case: c, invoice, selectedPaymentMethod: invoice?.paymentMethodId, draft };
}

const config: MachineConfig<EditCaseContext, any, EditCaseEvent> = {
  id: 'Edit Case Form',
  initial: 'loadCase',
  context: {
    stripePublishableKey: '',
    creditCards: [],
    userId: '',
    email: '',
    name: '',
    paymentType: PaymentType.CREDIT_CARD,
  } as any,
  states: {
    error: {
      on: {},
    },
    loadCase: {
      invoke: {
        src: loadCase,
        onDone: [
          {
            target: 'checkout',
            cond: (context, event) =>
              context.targetPage === 'checkout' &&
              guards.canEditCaseAutoLoadToCheckout(context, event),
            actions: 'loadData',
          },
          {
            target: 'creditCard',
            cond: (context, event) =>
              context.targetPage === 'payment' &&
              guards.canEditCaseAutoLoadToPayment(context, event),
            actions: 'loadData',
          },
          {
            target: 'selectPaymentType',
            cond: (context, event) =>
              context.targetPage === 'selectPaymentType' &&
              guards.canEditCaseAutoLoadToSelectPaymentType(context, event),
            actions: 'loadData',
          },
          {
            target: 'enterInfo',
            cond: (context, event) =>
              context.targetPage === 'enterInfo' &&
              guards.canEditCaseAutoLoadToEnterInfo(context, event),
            actions: 'loadData',
          },
          {
            target: 'caseType',
            actions: 'loadData',
          },
        ],
        onError: {
          actions: 'loadError',
        },
      },
    },
    caseType: {
      ...(caseTypeStates as any),
      entry: 'updateUrlToCaseType',
      on: {
        NEXT: 'enterInfo',
      },
    },
    enterInfo: {
      entry: 'updateUrlToEnterInfo',
      on: {
        NEXT: [
          {
            target: 'selectPaymentType',
            cond: { type: 'every', guards: ['isCaseInfoValid', 'doesNotNeedUpload'] },
            actions: ['setCaseInfo', 'saveDraft'],
          },
          {
            target: 'uploadingSites',
            cond: 'isCaseInfoValid',
            actions: 'setCaseInfo',
          },
        ],
        BACK: 'caseType',
      },
    },
    uploadingSites: {
      on: {
        SET_UPLOAD_PROGRESS: {
          actions: 'setUploadProgress',
        },
      },
      invoke: {
        src: 'uploadSites',
        onDone: [
          {
            target: 'enterInfo',
            cond: (_context, event) => {
              // Check if any site has failed images
              const sites = event.data;
              return sites.some((site: any) =>
                site.images.some((img: any) =>
                  typeof img === 'object' && 'status' in img && img.status === 'failed'
                )
              );
            },
            actions: 'setUploadedCaseInfo',
          },
          {
            target: 'selectPaymentType',
            actions: ['setUploadedCaseInfo', 'saveDraft'],
          },
        ],
        onError: {
          target: 'enterInfo',
          actions: (_context, event) => {
            console.error('Error uploading sites:', event.data);
          },
        },
      },
    },
    selectPaymentType: {
      ...(selectPaymentTypeStates as any),
      entry: 'updateUrlToSelectPaymentType',
      on: {
        NEXT: {
          target: 'creditCard',
          actions: 'saveDraft',
        },
        BACK: 'enterInfo',
      },
      onDone: {
        target: 'creditCard',
        actions: 'saveDraft',
      },
    },
    creditCard: {
      ...(creditCardStates as any),
      entry: 'updateUrlToPayment',
      onDone: {
        target: 'checkout',
        actions: 'saveDraft',
      },
      on: {
        BACK: [
          {
            target: 'enterInfo',
            cond: 'isInvoicingDisabled',
          },
          {
            target: 'selectPaymentType',
          },
        ],
      },
    },
    checkout: {
      ...(checkoutStates as any),
      entry: 'updateUrlToCheckout',
      onDone: {
        target: 'submitted',
        actions: 'clearDraft',
      },
      on: {
        BACK: 'creditCard',
      },
    },
    submitted: {
      type: 'final',
    },
  },
};

const submitCaseMachine = createMachine(config, {
  actions: {
    ...(actions as any),
    updateUrlToCaseType: actions.updateUrlPage('caseType'),
    updateUrlToEnterInfo: actions.updateUrlPage('enterInfo'),
    updateUrlToSelectPaymentType: actions.updateUrlPage('selectPaymentType'),
    updateUrlToPayment: actions.updateUrlPage('payment'),
    updateUrlToCheckout: actions.updateUrlPage('checkout'),
  },
  services: {
    loadCase,
    uploadSites: (context, _event) => (send) => {
      return uploadSitesWithProgress(context, send);
    },
  },
  guards: {
    ...guards,
    every: (ctx, event, { cond }) => {
      if (cond.type !== 'every') {
        throw new Error('Error: "every" condition expected type to be "every"');
      }
      return cond.guards.every((guardKey: string) => guards[guardKey](ctx, event));
    },
  },
});

export default submitCaseMachine;
